import type { ConversaResumo } from '@mymaxx/shared';
import { Clock, MessageSquare, User } from 'lucide-react';

/** Quanto tempo faz, em texto curto ("3 min", "2 h"). */
export function desde(iso: string | null): string {
  if (!iso) return '';
  const segundos = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (segundos < 60) return `${segundos}s`;
  if (segundos < 3600) return `${Math.floor(segundos / 60)} min`;
  if (segundos < 86_400) return `${Math.floor(segundos / 3600)} h`;
  return `${Math.floor(segundos / 86_400)} d`;
}

/** Lista lateral da caixa de entrada: minhas conversas ou a fila do setor. */
export function ListaConversas({
  conversas,
  selecionada,
  onSelecionar,
  vazio,
  mostrarAtendente = false,
}: {
  conversas: ConversaResumo[];
  selecionada: string | null;
  onSelecionar: (id: string) => void;
  vazio: string;
  /** Na lista do que está em curso no setor, quem atende é a informação que falta. */
  mostrarAtendente?: boolean;
}) {
  if (conversas.length === 0) {
    return <p className="px-4 py-6 text-center text-sm text-neutral-500">{vazio}</p>;
  }

  return (
    <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
      {conversas.map((c) => (
        <li key={c.id}>
          <button
            type="button"
            onClick={() => onSelecionar(c.id)}
            className={`w-full px-3 py-2.5 text-left transition ${
              selecionada === c.id
                ? 'bg-marca-50 dark:bg-marca-950'
                : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {c.cliente ?? 'Ainda sem identificação'}
              </span>
              {c.naoLidas > 0 && (
                <span className="shrink-0 rounded-full bg-marca-600 px-1.5 text-[11px] font-medium text-white">
                  {c.naoLidas}
                </span>
              )}
            </div>
            {/* Na busca por texto, o que a pessoa quer ver é o trecho que casou — a última
                mensagem do atendimento quase nunca é a que ela lembra. */}
            {c.trecho ? (
              <p className="truncate border-l-2 border-marca-400 pl-1.5 text-xs text-neutral-600 italic dark:text-neutral-300">
                {c.trecho}
              </p>
            ) : (
              <p className="truncate text-xs text-neutral-500">
                {c.ultimaMensagem ?? 'Sem mensagens'}
              </p>
            )}
            <div className="mt-1 flex items-center gap-2 text-[11px] text-neutral-400">
              <span className="flex items-center gap-1">
                <MessageSquare size={11} /> {c.setor?.nome ?? '—'}
              </span>
              {c.status === 'fila' ? (
                <span className="flex items-center gap-1 text-amber-600">
                  <Clock size={11} /> esperando há {desde(c.entrouNaFilaEm)}
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <User size={11} />
                  <span className="truncate">
                    {mostrarAtendente ? `${c.atendente?.nome ?? 'sem atendente'} · ` : ''}
                    {desde(c.ultimaMensagemEm ?? c.iniciadaEm)}
                  </span>
                </span>
              )}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}
